import React, { useEffect, useState } from "react";
import { fetchDailyThroughputDetails } from "../api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { DataGrid } from "@mui/x-data-grid";
import { useTheme } from '@mui/material/styles';
import getWorkcellColor from "../utils/getWorkcellColor";
import { useTranslation } from "react-i18next";
import { useCustomerLocale } from "../context/CustomerSettingsContext";

const DailyThroughputOverviewChart = ({ date, debug = false, onLoadingChange }) => {
  const [data, setData] = useState([]);
  const [workcellNames, setWorkcellNames] = useState({});
  const theme = useTheme();
  const { t } = useTranslation();
  const locale = useCustomerLocale();

  const formatCount = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return t("common.na");
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(num);
  };

  useEffect(() => {
    if (!date) {
      setData([]);
      onLoadingChange?.(false);
      return;
    }
    onLoadingChange?.(true);
    fetchDailyThroughputDetails(date)
      .then((apiData) => {
        if (apiData.length === 0) {
          setData([]);
          return;
        }
        // Create a full 24-hour dataset with default 0 values for each workcell
        const workcells = [...new Set(apiData.map((entry) => entry.workcell_id))];
        const workcellNamesMap = {};
        apiData.forEach((entry) => {
          workcellNamesMap[entry.workcell_id] = entry.workcell_name;
        });
        setWorkcellNames(workcellNamesMap);

        const fullDayData = Array.from({ length: 24 }, (_, i) => {
          const hour = i.toString().padStart(2, "0"); // "00" to "23"
          const entry = { hour };
          workcells.forEach((workcell) => {
            entry[workcell] = 0;
          });
          return entry;
        });

        // Merge API data into the full dataset
        apiData.forEach((entry) => {
          const index = fullDayData.findIndex((d) => d.hour === entry.hour);
          if (index !== -1) {
            fullDayData[index][entry.workcell_id] = entry.work_done; // Replace 0 with actual value
          }
        });

        setData(fullDayData);
      })
      .catch((error) => {
        console.error(error);
        setData([]);
      })
      .finally(() => {
        onLoadingChange?.(false);
      });
  }, [date]);

  const columns = [
    { field: "hour", headerName: t("daily.plannings.throughputOverview.hour"), width: 100 },
    ...Object.keys(workcellNames).map((workcell) => ({
      field: workcell,
      headerName: workcellNames[workcell],
      width: 150,
    })),
  ];

  const allWorkcellIds = data && data.length
    ? Object.keys(data[0])
        .filter(key => key !== "hour")
        .map(Number)
        .sort((a, b) => a - b)
    : [];

  return (
    <>
      {data.length === 0 ? (
        <div>{t("common.noData")}</div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
                <XAxis dataKey="hour" />
              <YAxis
                label={{
                  value: t("daily.plannings.throughputChart.axisLabel"),
                  angle: -90,
                  position: "insideLeft",
                  style: { textAnchor: "middle" }
                }}
              />
              <Tooltip formatter={(value) => formatCount(value)} />
              {data.length > 0 &&
                Object.keys(data[0])
                  .filter((key) => key !== "hour")
                  .filter(workcell => data.some(entry => entry[workcell] > 0)) // Only show workcells that have some non-zero values
                  .map((workcell, index) => (
                    <Bar
                      key={workcell}
                      dataKey={(entry) => (entry[workcell] > 0 ? entry[workcell] : null)}
                      name={workcellNames[workcell]}
                      fill={getWorkcellColor(workcell, theme.palette.chart.colors, allWorkcellIds)}
                      stackId="a"
                    />
                  ))}
            </BarChart>
          </ResponsiveContainer>

            {debug ? (
              <DataGrid
                rows={data}
                columns={columns}
                getRowId={(row) => row.hour}
                sortingMode="client"
                disableColumnMenu
                hideFooterPagination
                sx={{ flexGrow: 1 }}
                disableExtendRowFullWidth
              />
          ) : null}
        </>
      )}
    </>
  );
};

export default DailyThroughputOverviewChart;
